import moment from "moment-timezone";
import React from "react";
import styled from "styled-components";

import { CheckCircleInverted } from "src/static/icons";

type TProps = {
  departure: string;
  arrival: string;
  availableSeats: number;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
};

interface CardProps {
  selected?: boolean;
  disabled?: boolean;
  availableSeats?: number;
}

const BusCard: React.FC<TProps> = ({
  departure,
  arrival,
  availableSeats,
  selected,
  disabled,
  onClick,
}) => {
  return (
    <Card
      selected={selected}
      disabled={disabled}
      availableSeats={availableSeats}
      onClick={() => {
        if (!disabled && availableSeats !== 0) onClick();
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Availability
          selected={selected}
          disabled={disabled}
          availableSeats={availableSeats}
        >
          {availableSeats === 0
            ? "Unavailable"
            : availableSeats <= 5
            ? "Almost out"
            : "Available"}
        </Availability>
        {selected && <CheckCircleInverted />}
      </div>
      <TimesContainer>
        <div>
          <TimeTitle disabled={disabled}>Departure Time</TimeTitle>
          <Time disabled={disabled}>
            {moment(departure).tz("America/Toronto").format("h:mm A z")}
          </Time>
        </div>
        <div>
          <TimeTitle disabled={disabled}>Expected Arrival Time</TimeTitle>
          <Time disabled={disabled}>
            {moment(arrival).tz("America/Toronto").format("h:mm A z")}
          </Time>
        </div>
      </TimesContainer>
      <div style={{ width: "100%", borderBottom: "1px solid #D1D5DB" }} />
      <AvailableSeats
        selected={selected}
        disabled={disabled}
        availableSeats={availableSeats}
      >
        <p>Seats Available</p>
        <Seats
          selected={selected}
          disabled={disabled}
          availableSeats={availableSeats}
        >
          {availableSeats}
        </Seats>
      </AvailableSeats>
    </Card>
  );
};

const Card = styled.div<CardProps>`
  border-radius: 8px;
  border: 1px solid ${(props) => (props.selected ? "#2395C6" : "#d1d5db")};
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  min-width: 250px;
  max-width: max(360px, 50%);
  flex: 1 0 calc(50% - 8px);

  :hover {
    background-color: ${(props) => (props.disabled ? "" : "#f9fafb")};
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  }
`;

const Availability = styled.p<CardProps>`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) =>
    props.disabled && !props.selected
      ? "#9CA3AF"
      : (props.availableSeats ?? 0) <= 5
      ? "#D97706"
      : "#54C152"};
`;

const TimesContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const TimeTitle = styled.p<CardProps>`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#6b7280")};
`;

const Time = styled.h3<CardProps>`
  font-size: 20px;
  font-weight: 900;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "")};
`;

const Seats = styled.span<CardProps>`
  font-weight: 900;
  color: ${(props) =>
    props.disabled
      ? "#9CA3AF"
      : (props.availableSeats ?? 0) <= 5
      ? "#D97706"
      : "black"};
`;

const AvailableSeats = styled.div<CardProps>`
  font-size: 16px;
  font-weight: 500;
  display: flex;
  gap: 8px;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "")};
`;

export default BusCard;
